/** A form only claims an entry the *other* form wrote, within the TTL (SC 3.3.7). */

const STORAGE_KEY = 'zapier.auth.handoff-email';

export const HANDOFF_TTL_MS = 5 * 60 * 1000;

export type AuthEmailSurface = 'login' | 'sso';

type HandoffEntry = {
  email: string;
  from: AuthEmailSurface;
  at: number;
};

/** sessionStorage can throw (blocked site data), not just be empty; failure is non-fatal. */
function getStorage(): Storage | undefined {
  try {
    return globalThis?.sessionStorage;
  } catch {
    return undefined;
  }
}

function removeEntry(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal.
  }
}

function writeEntry(storage: Storage, entry: HandoffEntry): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Non-fatal.
  }
}

function readEntry(storage: Storage): HandoffEntry | undefined {
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;

  try {
    const entry = JSON.parse(raw) as Partial<HandoffEntry>;
    if (
      typeof entry?.email === 'string' &&
      entry.email &&
      (entry.from === 'login' || entry.from === 'sso') &&
      typeof entry.at === 'number'
    ) {
      return entry as HandoffEntry;
    }
  } catch {
    // Malformed: dropped below.
  }
  removeEntry(storage);
  return undefined;
}

export function rememberAuthEmail(
  surface: AuthEmailSurface,
  email: string
): void {
  const storage = getStorage();
  if (!storage) return;

  if (email) {
    writeEntry(storage, { email, from: surface, at: Date.now() });
  } else {
    removeEntry(storage);
  }
}

/** Own entry is ignored but left in place (StrictMode runs effects twice). */
export function claimAuthEmail(surface: AuthEmailSurface): string {
  const storage = getStorage();
  if (!storage) return '';

  const entry = readEntry(storage);
  if (!entry) return '';

  const age = Date.now() - entry.at;
  if (age < 0 || age > HANDOFF_TTL_MS) {
    removeEntry(storage);
    return '';
  }

  if (entry.from === surface) return '';

  // Re-stamp as ours so the user can bounce back.
  writeEntry(storage, { email: entry.email, from: surface, at: Date.now() });
  return entry.email;
}

export function forgetAuthEmail(): void {
  const storage = getStorage();
  if (!storage) return;

  removeEntry(storage);
}
